import { ReactElement } from 'react';
import { Card, Form } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useToggle } from 'react-use';
import { Advisor } from '@minutes-system/backend/src/api/advisors/schema';
import { Customer } from '@minutes-system/backend/src/api/customers/schema';
import { UpdateRecordingBody } from '@minutes-system/backend/src/api/recordings/schema';
import { determineCallDirection } from '@minutes-system/backend/src/api/recordings/callDirection';
import EditButton from './EditButton';
import RecordingInfoForm from './RecordingInfoForm';
import RecordingInfoDisplay from './RecordingInfoDisplay';
import { advisorDisplay } from '../../utils/advisorDisplay';
import { customerDisplay } from '../../utils/customerDisplay';

interface FormValues {
  title: string;
  advisorId: number;
  customerId: number;
  meetingDate: Dayjs;
  meetingTime: Dayjs;
}

const RECORDING_STATUS = {
  COMPLETED: 2,
} as const;

interface Props {
  title: string;
  advisor: { id: number; name: string; phone: string | null } | null;
  customer: { id: number; name: string; phone: string | null; mobilePhone: string | null } | null;
  meetingDateTime: Date;
  advisors: Advisor[];
  customers: Customer[];
  advisorPhone: string | null;
  customerPhone: string | null;
  originalFrom: string | null;
  originalTo: string | null;
  recordingStatusId: number;
  onUpdate: (data: UpdateRecordingBody) => Promise<{ success: boolean }>;
}

const RecordingInfo = ({
  title,
  advisor,
  customer,
  meetingDateTime,
  advisors,
  customers,
  advisorPhone,
  customerPhone,
  originalFrom,
  originalTo,
  recordingStatusId,
  onUpdate,
}: Props): ReactElement => {
  const [isEditing, toggleEditing] = useToggle(false);
  const [isSaving, toggleSaving] = useToggle(false);
  const [form] = Form.useForm<FormValues>();

  const callDirection = determineCallDirection(originalFrom, originalTo, advisor?.phone ?? null);
  const meetingDateTimeFormatted = dayjs(meetingDateTime).format('YYYY/MM/DD HH:mm:ss');

  const advisorOptions = advisors.map(a => ({ value: a.id, label: advisorDisplay(a.name, a.phone) }));
  const customerOptions = customers.map(c => ({
    value: c.id,
    label: customerDisplay(c.name, c.mobilePhone ?? c.phone),
  }));

  const handleEdit = (): void => {
    form.setFieldsValue({
      title,
      advisorId: advisor?.id,
      customerId: customer?.id,
      meetingDate: dayjs(meetingDateTime),
      meetingTime: dayjs(meetingDateTime),
    });
    toggleEditing(true);
  };

  const handleCancel = (): void => {
    toggleEditing(false);
    form.resetFields();
  };

  const handleSave = async (): Promise<void> => {
    try {
      const values = await form.validateFields();
      toggleSaving(true);

      // DatePickerとTimePickerの値を結合し、JSTとして解釈してUTCに変換
      const dateStr = values.meetingDate.format('YYYY-MM-DD');
      const timeStr = values.meetingTime.format('HH:mm:ss');
      const meetingDateTimeValue = new Date(`${dateStr}T${timeStr}+09:00`);

      const result = await onUpdate({
        title: values.title,
        advisorId: values.advisorId,
        customerId: values.customerId,
        meetingDateTime: meetingDateTimeValue,
      });

      toggleSaving(false);

      if (result.success) {
        toggleEditing(false);
      }
    } catch {
      // バリデーションエラー時は何もしない
    }
  };

  const isEditable = recordingStatusId === RECORDING_STATUS.COMPLETED;
  const cardExtra = !isEditing && isEditable ? <EditButton onClick={handleEdit} /> : null;

  return (
    <Card title='録音情報' size='small' extra={cardExtra}>
      {isEditing ? (
        <RecordingInfoForm
          form={form}
          advisorOptions={advisorOptions}
          customerOptions={customerOptions}
          onSave={() => {
            void handleSave();
          }}
          onCancel={handleCancel}
          loading={isSaving}
        />
      ) : (
        <RecordingInfoDisplay
          title={title}
          advisor={advisorDisplay(advisor?.name ?? null, advisorPhone)}
          customer={customerDisplay(customer?.name ?? null, customerPhone)}
          meetingDateTime={meetingDateTimeFormatted}
          callDirection={callDirection}
        />
      )}
    </Card>
  );
};

export default RecordingInfo;
